//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React from 'react';
import { useAuth } from 'react-oidc-context';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useConfig } from '../../Config';
import { useUserAuth } from '../../UserAuth';
import Loading from '../loading/loading';
import { isSafeRedirectUrl } from '../../utils/validateUrlUtils';

function Login() {
  const auth = useAuth();
  const { isAuthenticating, isAuthenticated, isLoading } = useUserAuth();

  const [searchParams] = useSearchParams();
  const raw = searchParams.get('redirectLocation') || '/';
  const redirectLocation = isSafeRedirectUrl(raw) ? raw : '/';

  const { baseName } = useConfig();

  if (isAuthenticating) {
    return <Loading />;
  }

  if (auth.error) {
    if (!auth.isLoading) {
      auth.clearStaleState().then(() => {
        auth.signinRedirect();
      });
    }
  }

  if (!isAuthenticated) {
    const url = new URL(redirectLocation, window.location.origin);
    const redirectPath = url.pathname;

    // The URL fragment (e.g. Plan record hash `#/ccmweb/.../SCRUM00018807`)
    // MUST NOT be included in the OAuth redirect_uri. OIDC authorization
    // servers do not preserve the fragment component of a redirect URI
    // (RFC 6749 §3.1.2); Keycloak drops it and clobbers it with the `code`/
    // `state` response params on the round-trip. Sending it here is exactly
    // what makes deep links land on the app home page instead of the target
    // record after a Keycloak login/refresh.
    //
    // Instead we build redirect_uri from path + search only, and carry the
    // fragment through the round-trip via the OIDC `state` (round-tripped by
    // oidc-client-ts and restored in onSigninCallback in index.tsx).
    const redirectPathWithSearch = `${url.pathname}${url.search}`;

    const redirectUri =
      redirectPath === `/logout`
        ? `${window.location.protocol}//${window.location.hostname}${baseName}`
        : `${window.location.protocol}//${window.location.hostname}${baseName}${redirectPathWithSearch}`;

    auth.signinRedirect({
      redirect_uri: redirectUri,
      // Preserve the intended fragment across the Keycloak round-trip.
      ...(url.hash ? { state: { fragment: url.hash } } : {}),
    });
    return <Loading />;
  }

  if (isLoading) {
    return <Loading />;
  }

  return <Navigate to={redirectLocation} />;
}

export default Login;
